import { useMemo } from "react";

let elementCounters = {};

export function resetElementCounters(handler) {
  elementCounters = {};
  return handler();
}

function getCounterBySignature(typeName, signature) {
  const typeCounter = elementCounters[typeName] || { value: 0, signatures: {} };
  if (typeof typeCounter.signatures[signature] === "number") {
    return typeCounter.signatures[signature];
  }
  const { value, signatures } = typeCounter;
  elementCounters[typeName] = {
    signatures: { ...signatures, [signature]: value },
    value: value + 1,
  };
  return value;
}

function sortPropEntries([nameA], [nameB]) {
  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  return 0;
}

function decycle(object, replacer) {
  const objects = new WeakMap();

  return (function derez(val, path) {
    let oldPath; // The path of an earlier occurance of value
    let nu; // The new object or array
    const value = replacer !== undefined ? replacer(val) : val;
    if (
      typeof value === "object" &&
      value !== null &&
      !(value instanceof Boolean) &&
      !(value instanceof Date) &&
      !(value instanceof Number) &&
      !(value instanceof RegExp) &&
      !(value instanceof String)
    ) {
      oldPath = objects.get(value);
      if (oldPath !== undefined) {
        return { $ref: oldPath };
      }
      objects.set(value, path);
      if (Array.isArray(value)) {
        nu = [];
        value.forEach(function nuArrayElement(element, i) {
          nu[i] = derez(element, `${path}[${i}]`);
        });
      } else {
        nu = {};
        Object.keys(value).forEach(function nuObjectProp(name) {
          nu[name] = derez(value[name], `${path}[${JSON.stringify(name)}]`);
        });
      }
      return nu;
    }
    return value;
  })(object, "$");
}

function safeJSONStringify(value) {
  try {
    return JSON.stringify(decycle(value));
  } catch (error) {
    return error.message;
  }
}

const getNodeSignature = (node) => {
  if (!node) return "";
  if (["string", "number"].includes(typeof node)) return node;
  if (node instanceof Array) return node.map(getNodeSignature).join("");
  if (!node.props) return "";
  const { children, ...propsWithoutChildren } = node.props;
  return Object.entries(propsWithoutChildren)
    .sort(sortPropEntries)
    .map(([name, value]) => [
      name,
      typeof value !== "function" ? safeJSONStringify(value) : "function",
    ])
    .map((entry) => entry.join("="))
    .join("&")
    .concat(getNodeSignature(children));
};

export function useUniqueId(typeName, props) {
  const index = useMemo(() => {
    return getCounterBySignature(typeName, getNodeSignature({ props }));
  }, [elementCounters]);
  return index;
}
